'use client';

import { BigNum, SpotMarketConfig } from '@drift-labs/sdk';
import { AprDetails } from './types';

import { getMarketIconSrc } from 'src/components/Utils/MarketIcon';
import { getApyFromApr } from 'src/utils/borrowLend';
import { toFixedLocaleString } from 'src/utils/formatting';
import { BorrowMarketData } from '../useSyncBorrowLendBalances';

/**
 * Safely calculates APR from annual bonus and total deposits, capping at 1000%
 * @param annualBonus The annual bonus amount
 * @param totalDeposits The total deposits amount
 * @returns The calculated APR percentage, capped at 1000%
 */
const calculateSafeApr = (
	annualBonus: number,
	totalDeposits: number
): number => {
	if (
		!isFinite(annualBonus) ||
		!isFinite(totalDeposits) ||
		totalDeposits <= 0
	) {
		return 0;
	}
	const apr = (annualBonus / totalDeposits) * 100;
	// Cap at 1000% APR to prevent infinity issues
	return Math.min(apr, 1000);
};

// Static reward definitions

/**
 * Get USDe static reward metric
 */
export function getUsdeRewardMetric(): AprDetails[] {
	return [
		{
			type: 'lend-reward' as const,
			source: 'Ethena Points',
			apy: 0,
			hideYield: true,
			icon: getMarketIconSrc({ baseSymbol: 'usde' }),
			onlyText: true,
			helperText:
				'Earn 20 Sats a day per USDe. Learn More at https://www.drift.trade/updates/drift-partners-with-ethena-to-launch-usde-and-susde-on-solana',
		},
	];
}

// Reward calculation functions

/**
 * Calculate USDS rewards
 */
export function calculateUsdsRewards(
	borrowLendData: BorrowMarketData | undefined,
	usdsBonusPerWeek: number
): AprDetails[] {
	if (!borrowLendData || !usdsBonusPerWeek) return [];

	const usdsAnnualBonus = BigNum.fromPrint(
		`${usdsBonusPerWeek * 52}`,
		borrowLendData?.bankConfig.precisionExp
	);

	const usdsApr = calculateSafeApr(
		usdsAnnualBonus.toNum(),
		borrowLendData?.totalDepositsBase?.toNum() ?? 0
	);

	return [
		{
			type: 'lend-reward',
			source: 'USDS Rewards',
			apy: getApyFromApr(usdsApr, 'hourly'),
			apr: usdsApr,
			icon: getMarketIconSrc({ baseSymbol: 'usds' }),
			helperText: `(${toFixedLocaleString(
				usdsBonusPerWeek / 7,
				2
			)} USDS daily)`,
		},
	];
}

/**
 * Calculate EURC rewards
 */
export function calculateEurcRewards(
	borrowLendData: BorrowMarketData | undefined,
	eurcBonusPerWeek: number
): AprDetails[] {
	if (!borrowLendData || !eurcBonusPerWeek) return [];

	const eurcAnnualBonus = BigNum.fromPrint(
		`${eurcBonusPerWeek * 52}`,
		borrowLendData?.bankConfig.precisionExp
	);

	const totalBorrowsBase = borrowLendData?.totalBorrowsBase ?? BigNum.zero();
	const minBorrowThreshold = BigNum.fromPrint(
		'1000',
		totalBorrowsBase.precision
	);

	const eurcApr = totalBorrowsBase.gt(minBorrowThreshold)
		? Math.min(
				(eurcAnnualBonus.toNum() / totalBorrowsBase.toNum()) * 100,
				10000
			)
		: 0;

	const finalApr = isFinite(eurcApr) && !isNaN(eurcApr) ? eurcApr : 0;
	const finalApy =
		isFinite(finalApr) && !isNaN(finalApr)
			? getApyFromApr(finalApr, 'hourly')
			: 0;

	return [
		{
			type: 'borrow-reward',
			source: 'EURC Rewards',
			apy: finalApy,
			apr: finalApr,
			icon: getMarketIconSrc({ baseSymbol: 'eurc' }),
			helperText: `(${toFixedLocaleString(
				eurcBonusPerWeek / 7,
				2
			)} EURC daily)`,
		},
	];
}

/**
 * Calculate Exponent Pool JitoSOL JTO rewards
 */
export function calculateExponentPoolJtoRewards(
	borrowLendData: BorrowMarketData | undefined,
	jtoBonusPerWeek: number,
	jtoSpotMarket: SpotMarketConfig,
	oraclePrice: number
): AprDetails[] {
	if (!borrowLendData || !jtoBonusPerWeek || !jtoSpotMarket) return [];

	const jtoAnnualBonus = BigNum.fromPrint(
		`${jtoBonusPerWeek * 52}`,
		jtoSpotMarket.precisionExp
	);

	const notionalJtoAnnualBonus = jtoAnnualBonus.toNum() * (oraclePrice ?? 0);

	const jitoSOLApr = calculateSafeApr(
		notionalJtoAnnualBonus,
		borrowLendData?.totalDepositsQuote?.toNum() ?? 0
	);

	return [
		{
			type: 'lend-reward',
			source: 'JTO Rewards',
			apy: getApyFromApr(jitoSOLApr, 'hourly'),
			apr: jitoSOLApr,
			icon: getMarketIconSrc({ baseSymbol: 'jto' }),
			helperText: `(${toFixedLocaleString(jtoBonusPerWeek / 7, 2)} JTO daily)`,
			showBoostNumberInline: true,
		},
	];
}

/**
 * Calculate syrupUSDC USDC rewards (OI-based)
 */
export function calculateSyrupUsdcRewards(
	borrowLendData: BorrowMarketData | undefined,
	usdcBonusPerWeek: number,
	usdcSpotMarket: SpotMarketConfig
): AprDetails[] {
	if (!borrowLendData || !usdcBonusPerWeek || !usdcSpotMarket) return [];

	const usdcAnnualBonus = BigNum.fromPrint(
		`${usdcBonusPerWeek * 52}`,
		usdcSpotMarket.precisionExp
	);

	const syrupUsdcApr = calculateSafeApr(
		usdcAnnualBonus.toNum(),
		borrowLendData?.totalDepositsQuote?.toNum() ?? 0
	);

	const syrupUsdcApy = getApyFromApr(syrupUsdcApr, 'hourly');

	return [
		{
			type: 'lend-oi-reward',
			source: 'USDC Rewards',
			apy: syrupUsdcApy,
			perpsCollateralReward: {
				apy: syrupUsdcApy,
				dailyReward: Math.trunc(usdcBonusPerWeek / 7),
				rewardMarketSymbol: 'USDC',
				learnMoreLink:
					'https://docs.drift.trade/lend-borrow/rewards#active-deposit-rewards',
				collateralMarketSymbol: 'syrupUSDC',
			},
		},
	];
}

/**
 * Calculate LBTC USDC rewards (OI-based)
 */
export function calculateLbtcUsdcRewards(
	borrowLendData: BorrowMarketData | undefined,
	usdcBonusPerWeek: number,
	usdcSpotMarket: SpotMarketConfig
): AprDetails[] {
	if (!borrowLendData || !usdcBonusPerWeek || !usdcSpotMarket) return [];

	const usdcAnnualBonus = BigNum.fromPrint(
		`${usdcBonusPerWeek * 52}`,
		usdcSpotMarket.precisionExp
	);

	const lbtcApr = calculateSafeApr(
		usdcAnnualBonus.toNum(),
		borrowLendData?.totalDepositsQuote?.toNum() ?? 0
	);

	const lbtcApy = getApyFromApr(lbtcApr, 'hourly');

	return [
		{
			type: 'lend-oi-reward',
			source: 'USDC Rewards',
			apy: lbtcApy,
			perpsCollateralReward: {
				apy: lbtcApy,
				dailyReward: Math.trunc(usdcBonusPerWeek / 7),
				rewardMarketSymbol: 'USDC',
				learnMoreLink:
					'https://docs.drift.trade/lend-borrow/rewards#active-deposit-rewards',
				collateralMarketSymbol: 'LBTC',
			},
		},
	];
}

/**
 * Calculate CASH rewards
 */
export function calculateCashRewards(
	borrowLendData: BorrowMarketData | undefined,
	cashBonusPerWeek: number
): AprDetails[] {
	if (!borrowLendData || !cashBonusPerWeek) return [];

	const cashAnnualBonus = BigNum.fromPrint(
		`${cashBonusPerWeek * 52}`,
		borrowLendData?.bankConfig.precisionExp
	);

	const cashApr = calculateSafeApr(
		cashAnnualBonus.toNum(),
		borrowLendData?.totalDepositsBase?.toNum() ?? 0
	);

	return [
		{
			type: 'lend-reward',
			source: 'CASH Rewards',
			apy: getApyFromApr(cashApr, 'hourly'),
			apr: cashApr,
			icon: getMarketIconSrc({ baseSymbol: 'cash' }),
			helperText: `(${toFixedLocaleString(
				cashBonusPerWeek / 7,
				2
			)} CASH daily)`,
		},
	];
}

/**
 * Calculate CASH borrow rewards
 */
export function calculateCashBorrowRewards(
	borrowLendData: BorrowMarketData | undefined,
	cashBorrowBonusPerWeek: number
): AprDetails[] {
	if (!borrowLendData || !cashBorrowBonusPerWeek) return [];

	const cashAnnualBonus = BigNum.fromPrint(
		`${cashBorrowBonusPerWeek * 52}`,
		borrowLendData?.bankConfig.precisionExp
	);

	const totalBorrowsBase = borrowLendData?.totalBorrowsBase ?? BigNum.zero();
	const minBorrowThreshold = BigNum.fromPrint(
		'1000',
		totalBorrowsBase.precision
	);

	const cashApr = totalBorrowsBase.gt(minBorrowThreshold)
		? Math.min(
				(cashAnnualBonus.toNum() / totalBorrowsBase.toNum()) * 100,
				10000
			)
		: 0;

	const finalApr = isFinite(cashApr) && !isNaN(cashApr) ? cashApr : 0;
	const finalApy =
		isFinite(finalApr) && !isNaN(finalApr)
			? getApyFromApr(finalApr, 'hourly')
			: 0;

	return [
		{
			type: 'borrow-reward',
			source: 'CASH Rewards',
			apy: finalApy,
			apr: finalApr,
			icon: getMarketIconSrc({ baseSymbol: 'cash' }),
			helperText: `(${toFixedLocaleString(
				cashBorrowBonusPerWeek / 7,
				2
			)} CASH daily)`,
		},
	];
}
